// @ts-nocheck
if(!customElements.get('product-media-gallery')) {
    /**
     * Handleizes an option value the way Liquid's `handle` filter does, so a selection
     * read from the form compares equal to the identity `gallery-slide-identity` stamped
     * on the slides.
     *
     * @param {string} value - The option value, as the customer sees it.
     * @returns {string} The handle.
     */
    const optionHandle = (value) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    /**
     * Product gallery whose slides are filtered by the selected orientation, style
     * and mat border.
     *
     * Swiper cannot lay out slides that are hidden with CSS: with a numeric
     * `slidesPerView` it gives every slide the same computed size regardless of its
     * display, so a hidden slide still owns a snap position. The gallery therefore
     * used to slide onto a blank slide and then correct itself with a second
     * `slideTo` - the jump customers were seeing. Non-matching slides are now moved
     * out of the swiper wrapper into a parked container instead, so Swiper only ever
     * knows about the slides that match the current selection.
     */
    class ProductMediaGallery extends HTMLElement {
        constructor() {
            super();
            this.onChangedGalleryImageUnsubscriber = undefined;
            this.onGalleryImageChangedUnsubcriber = undefined;
            this.onBorderMatChangedUnsubscriber = undefined;
            this.swiperThumbnails = null;
            this.swiperMain = null;
            this.productDataCache = null;
            this.lastPathname = '';
            // Every slide in Liquid render order, whether parked or not.
            this.allMainSlides = [];
            this.allThumbSlides = [];
            this.parkedMain = null;
            this.parkedThumbs = null;
        }
        async connectedCallback() {
            // 2. Cache product data
            const currentPath = window.location.pathname;
            if (!this.productDataCache || this.lastPathname !== currentPath) {
                try {
                    const res = await fetch(`${currentPath}.json`);
                    this.productDataCache = await res.json();
                    this.lastPathname = currentPath;
                } catch (err) {
                    console.error('Fetch error:', err);
                    return;
                }
            }
            this.onChangedGalleryImageUnsubscriber = subscribe(
                PUB_SUB_EVENTS.changedGalleryImage,
                this._findVariantImage.bind(this)
            );
            this.onGalleryImageChangedUnsubcriber = subscribe(
                PUB_SUB_EVENTS.galleryImageChanged,
                this._setGalleryImage.bind(this)
            );
            this.onBorderMatChangedUnsubscriber = subscribe(
                PUB_SUB_EVENTS.borderMatChanged,
                this._borderMatChanged.bind(this)
            );
            this.swiperThumbnails = new Swiper(".gallery-thumbnails", {
                spaceBetween: 10,
                watchSlidesProgress: true,
                slidesPerView: 6.6,
                breakpoints: {
                    0: {
                        slidesPerView: 5.2,
                        spaceBetween: 8,

                    },
                    768: {
                        slidesPerView: 6.6,
                        spaceBetween: 10,
                    }
                }
            });
            this.swiperMain = new Swiper(".main-gallery", {
                spaceBetween: 10,
                navigation: {
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                },
                thumbs: {
                    swiper: this.swiperThumbnails,
                },
                on: {
                    slideChange: () => this._syncThumbnailScroll(),
                }
            });

            // Nothing is parked yet, so the swipers currently hold every slide.
            this.allMainSlides = [...this.swiperMain.slides];
            this.allThumbSlides = [...this.swiperThumbnails.slides];
            this.parkedMain = this._createParkedContainer();
            this.parkedThumbs = this._createParkedContainer();

            this._applyFilter();
            this._slideToDefault();

            this.swiperMain.el.classList.add('active');
            this.swiperThumbnails.el.classList.add('active');
        }

        disconnectedCallback() {
            this.onChangedGalleryImageUnsubscriber?.();
            this.onGalleryImageChangedUnsubcriber?.();
            this.onBorderMatChangedUnsubscriber?.();
        }

        /**
         * Creates the off-swiper container that holds the slides which do not match
         * the current selection.
         */
        _createParkedContainer() {
            const container = document.createElement('div');
            container.className = 'gallery-parked-slides';
            container.hidden = true;
            this.appendChild(container);
            return container;
        }

        /**
         * Whether a slide belongs to an orientation, style and mat border.
         *
         * @param {HTMLElement} slide - The slide to test, carrying the identity
         * attributes `gallery-slide-identity` stamped on it.
         * @param {{orientation: string, style: string, matBorder: string}} [selection] - The
         * selection to test against. Defaults to the one the swiper is filtered on.
         * @returns {boolean} Whether the slide should stay in the swiper.
         */
        _matchesSelection(slide, selection = this.swiperMain.el.dataset) {
            const { orientation, style, matBorder } = selection;

            // A product without an orientation option has nothing to filter on.
            if (orientation === 'false') return true;

            const { slideRole, slideOrientation } = slide.dataset;

            if (slideOrientation !== orientation) return false;
            if (matBorder !== 'false' && !this._slideHas(slide, 'slideMats', matBorder)) return false;

            // The angled and room shots are shown whichever style is selected, so only the
            // mock-ups - the slides the artwork is simulated into - are narrowed by style.
            if (slideRole !== 'main') return true;

            return style === 'false' || this._slideHas(slide, 'slideStyles', style);
        }

        /**
         * Whether one of a slide's space separated identity lists contains a handle. A room
         * shot is shared between mat borders, so these are lists rather than single values.
         *
         * @param {HTMLElement} slide - The slide to read.
         * @param {string} key - The dataset key holding the list.
         * @param {string} handle - The handle to look for.
         * @returns {boolean} Whether the slide is tagged with the handle.
         */
        _slideHas(slide, key, handle) {
            return (slide.dataset[key] || '').split(' ').includes(handle);
        }

        /**
         * Moves slides between the swiper wrappers and the parked containers so the
         * wrappers only hold slides matching the current selection, then lets both
         * swipers recalculate.
         */
        _applyFilter() {
            const activeMainSlide = this.swiperMain.slides[this.swiperMain.activeIndex];

            this._partition(this.allThumbSlides, this.swiperThumbnails, this.parkedThumbs);
            this._partition(this.allMainSlides, this.swiperMain, this.parkedMain);

            // `update()` recalculates sizes but never re-reads the wrapper's children,
            // so `recalcSlides()` has to run first for the moves to be picked up.
            this.swiperThumbnails.recalcSlides();
            this.swiperThumbnails.update();
            this.swiperMain.recalcSlides();
            this.swiperMain.update();

            // Stay on the same image whenever it survived the filter.
            const restoredIndex = this.swiperMain.slides.indexOf(activeMainSlide);
            this.swiperMain.slideTo(restoredIndex > -1 ? restoredIndex : 0, 0, false);
        }

        /**
         * Splits slides between a swiper wrapper and its parked container, keeping
         * the wrapper in Liquid render order.
         *
         * @param {HTMLElement[]} slides - Every slide, in their original order.
         * @param {Swiper} swiper - The swiper that owns the matching slides.
         * @param {HTMLElement} parked - The off-swiper container for the rest.
         */
        _partition(slides, swiper, parked) {
            const wrapper = swiper.slidesEl;
            let previous = null;

            for (const slide of slides) {
                if (this._matchesSelection(slide)) {
                    // Re-inserting a slide that already sits in the right place would
                    // needlessly disconnect and reconnect the canvas element inside it.
                    const next = previous ? previous.nextElementSibling : wrapper.firstElementChild;

                    if (slide !== next) wrapper.insertBefore(slide, next);

                    previous = slide;
                } else if (slide.parentElement !== parked) {
                    parked.appendChild(slide);
                }
            }
        }

        /**
         * Opens the gallery on the mock-up for the current selection - the same slide the
         * cart preview is rendered from, so the customer lands on what they will be buying.
         */
        _slideToDefault() {
            const index = this.swiperMain.slides.findIndex((slide) =>
                slide.dataset.slideRole === 'main' && this._matchesSelection(slide)
            );

            this.swiperMain.slideTo(index > -1 ? index : 0, 0, false);
        }

        /**
         * Keeps the thumbnail strip scrolled to the active thumbnail.
         */
        _syncThumbnailScroll() {
            const thumbSwiper = this.swiperMain?.thumbs?.swiper;
            if (!thumbSwiper) return;

            const activeIndex = this.swiperMain.activeIndex;

            // Get actual rendered slides per view
            const visibleThumbs = thumbSwiper.slidesPerViewDynamic
                                || thumbSwiper.params.slidesPerView;

            // Convert to number if it's 'auto'
            const visibleThumbsCount = typeof visibleThumbs === 'number'
                                    ? visibleThumbs
                                    : Math.floor(thumbSwiper.width / thumbSwiper.slides[0]?.swiperSlideSize);

            if (activeIndex >= thumbSwiper.activeIndex + visibleThumbsCount - 2) {
                thumbSwiper.slideTo(activeIndex - 2);
            }

            if (activeIndex < thumbSwiper.activeIndex) {
                thumbSwiper.slideTo(Math.max(activeIndex - 2, 0));
            }
        }

        /**
         * Resolves the swiper index of a slide. `data-index` is the Liquid render
         * order, which no longer matches the swiper index once slides are parked.
         *
         * @param {HTMLElement | null | undefined} slide - The slide to look up.
         * @returns {number} The swiper index, or -1 when the slide is parked.
         */
        _mainIndexOf(slide) {
            return slide ? this.swiperMain.slides.indexOf(slide) : -1;
        }

        /**
         * The selection the customer has made in the variant picker, in the shape the
         * slide filter expects. Reading the checked inputs rather than the swiper's own
         * dataset keeps the answer tied to what was configured, not to what the swiper
         * was last told.
         *
         * @returns {{orientation: string, style: string, matBorder: string} | null} The
         * selection, or null when the product has no orientation option to resolve against.
         */
        _formSelection() {
            const form = this.closest('.shopify-section')?.querySelector('.variant-picker__form') ||
                document.querySelector('.variant-picker__form');
            const orientation = form?.querySelector('[data-option-handle="orientation"] input:checked');

            if (!orientation) return null;

            const style = form.querySelector('[data-option-handle="style"] input:checked');
            const matBorder = form.querySelector('[data-option-handle="border-mat-options"] input:checked');
            const handle = (input) => (input ? optionHandle(input.value) : 'false');

            return { orientation: handle(orientation), style: handle(style), matBorder: handle(matBorder) };
        }

        /**
         * Whether a slide is the one the customer configured, on every option they chose.
         *
         * This is stricter than the gallery filter: that one keeps the angled shots of
         * every style in the strip so the customer can browse them, while this one has to
         * name a single slide, so the style has to match as well. The room shots state no
         * style, which is what keeps them out.
         *
         * @param {HTMLElement} slide - The slide to test.
         * @param {{orientation: string, style: string, matBorder: string}} selection - The
         * selection made in the variant picker.
         * @returns {boolean} Whether the slide shows exactly that selection.
         */
        _matchesSelectionExactly(slide, { orientation, style, matBorder }) {
            if (slide.dataset.slideOrientation !== orientation) return false;
            if (style !== 'false' && !this._slideHas(slide, 'slideStyles', style)) return false;

            return matBorder === 'false' || this._slideHas(slide, 'slideMats', matBorder);
        }

        /**
         * The slide the cart preview image is rendered from.
         *
         * The customer can swipe the gallery onto any thumbnail, so the active slide says
         * where they are looking rather than what they configured. This resolves the slide
         * from the variant picker instead - the one whose stated orientation, style and mat
         * border are the ones that were selected.
         *
         * The angled shot is preferred over the flat mock-up: it is the photograph of the
         * frame itself, so it is what the customer recognises in their cart. The mock-up is
         * the fallback for a selection that has no angled shot of its own.
         *
         * @returns {HTMLElement | null} The slide to render from, or null when the product
         * has no orientation option or nothing matches the selection.
         */
        getSelectedPreviewSlide() {
            const selection = this._formSelection();

            if (!selection) return null;

            // Parked slides are still candidates: they keep painting while parked, and a
            // stale filter must not decide what the customer bought.
            const slides = this.allMainSlides.length ? this.allMainSlides : [...(this.swiperMain?.slides ?? [])];
            const matching = slides.filter((slide) => this._matchesSelectionExactly(slide, selection));
            const byRole = (role) => matching.find((slide) => slide.dataset.slideRole === role);

            return byRole('sub') ?? byRole('main') ?? null;
        }

        async _findVariantImage({ data: { event } }) {
            const orientationIsPresent = event.target.closest('.variant-picker__form')?.querySelector('[data-option-handle="orientation"]');
            const selectedOptionsInputs = document.querySelectorAll('.variant-option__button-label input:checked');
            const selectedMatOptionInput = document.querySelector('[data-option-handle="border-mat-options"] input:checked');
            const styleIsPresent = document.querySelector('[data-option-handle="style"] input:checked');
            const selectedOptionsValue = [];
            selectedOptionsInputs.length > 0 && Array.from(selectedOptionsInputs).map(input => {
                selectedOptionsValue.push(input.value);
            })
            const marBorderRef = document.querySelector('[ref="mat_border"]');
            if(selectedMatOptionInput && marBorderRef) {
                marBorderRef.name = 'properties[Mat Border]';
                marBorderRef.value = selectedMatOptionInput.value;
            } else {
                marBorderRef.name = 'property[Mat Border]';
            }
            this.swiperMain.el.dataset.matBorder = selectedMatOptionInput ? optionHandle(selectedMatOptionInput.value) : false;
            this.swiperMain.el.dataset.orientation = orientationIsPresent ? optionHandle(selectedOptionsValue[0]) : false;
            this.swiperMain.el.dataset.style = styleIsPresent ? optionHandle(styleIsPresent.value) : false;

            // The selection changed, so refresh which slides the swipers own before
            // anything below looks up a slide index.
            this._applyFilter();

            // 1. Fast path for empty selections
            if (!selectedOptionsValue?.length) return;

            // 3. Fast exit if no variants
            const variants = this.productDataCache.product?.variants;
            if (!variants || !variants.length) return;

            // 4. Pre-process search terms once
            const searchTerms = new Set(
                selectedOptionsValue.map(opt => opt.toLowerCase().trim())
            );

            // 5. Optimized search using for...of loop
            let matchedVariant = null;
            for (const variant of variants) {
                const variantTitle = variant.title.toLowerCase();
                let allMatch = true;

                for (const term of searchTerms) {
                    if (!variantTitle.includes(term)) {
                        allMatch = false;
                        break; // Early exit inner loop
                    }
                }

                if (allMatch) {
                    matchedVariant = variant;
                    break; // Early exit outer loop
                }
            }
            // 6. Fast DOM lookup
            let matImageId = [];
            const optionMatIsPresent = document.querySelector('[data-option-handle="border-mat-options"]');
            const optionStyleIsPresent = document.querySelector('[data-option-handle="style"] input:checked');
            if(this.swiperMain.el.dataset.matBorder !== 'false' && optionMatIsPresent && optionStyleIsPresent) {
                const styleHandle = optionStyleIsPresent.value.toLowerCase().replaceAll(' ','-');
                matImageId = (event.target.closest('[data-option-handle="style"]') || event.target.closest('[data-option-handle="border-mat-options"]')) ? this.swiperMain.slides.filter(slide => slide.dataset.galleryImage.includes(styleHandle) && slide.dataset.galleryImage.includes(this.swiperMain.el.dataset.orientation) && slide.dataset.galleryImage.includes(this.swiperMain.el.dataset.matBorder) && slide.dataset.galleryImage.includes('sub')) : this.swiperMain.slides.filter(slide => slide.dataset.galleryImage.includes(styleHandle) && slide.dataset.galleryImage.includes(this.swiperMain.el.dataset.orientation) && slide.dataset.galleryImage.includes(this.swiperMain.el.dataset.matBorder) && slide.dataset.galleryImage.includes('main'));
            } else {
                const styleHandle = optionStyleIsPresent.value.toLowerCase().replaceAll(' ','-');
                matImageId = (event.target.closest('[data-option-handle="border-options"]') || optionStyleIsPresent === 'stretched-canvas' && !event.target.closest('[data-option-handle="orientation"]') || event.target.closest('[data-option-handle="style"]')) ? this.swiperMain.slides.filter(slide => slide.dataset.galleryImage.includes('sub') && slide.dataset.galleryImage.includes(styleHandle) && slide.dataset.galleryImage.includes(this.swiperMain.el.dataset.orientation) && slide.dataset.galleryImage.includes('border-wrap')) : this.swiperMain.slides.filter(slide => slide.dataset.galleryImage.includes('main') && slide.dataset.galleryImage.includes(styleHandle) && slide.dataset.galleryImage.includes(this.swiperMain.el.dataset.orientation));
            }

            if (matchedVariant?.image_id && this.swiperMain) {
                const variantSlide = this.querySelector(`.main-gallery [data-image-id="${matchedVariant.image_id}"]`);
                const targetIndex = this._mainIndexOf(matImageId[0] || variantSlide);

                if (targetIndex > -1) this.swiperMain.slideTo(targetIndex);
            }
        }
        async _setGalleryImage({data: { galleryImage }}) {
            const slideGalleryImage = this.querySelector(`.main-gallery [data-gallery-image="files/${galleryImage}"]`);
            const targetIndex = this._mainIndexOf(slideGalleryImage);

            if (targetIndex > -1) this.swiperMain.slideTo(targetIndex);
        }
        _borderMatChanged({data: { event, orientation, productStyleData }}) {
            const currentStyle = document.querySelector('[data-option-handle="style"] input:checked');
            const styleHandle = currentStyle.value.toLowerCase().replaceAll(' ','-');
            const visibleThumbnails = this.swiperMain.slides.filter(slide => slide.dataset.galleryImage.includes(this.swiperMain.el.dataset.matBorder) && slide.dataset.galleryImage.includes(orientation) && slide.dataset.galleryImage.includes(styleHandle));
            const targetIndex = this._mainIndexOf(visibleThumbnails[0]);
            targetIndex > -1 && this.swiperMain.slideTo(targetIndex);
            const matHandle = event.target.value.toLowerCase().replaceAll(' ','-');
            const styleImages = productStyleData.filter(style => style.image.reference.image.url.includes(matHandle));
            const styleInputs = document.querySelectorAll('[data-option-handle="style"] .variant-option__button-label input');
            if(styleInputs.length > 0) {
                styleInputs.forEach(input => {
                    const imgSrc = styleImages.filter(image => image.label.value.includes(input.value));
                    const spanImage = input.closest('label').querySelector('.swatch');
                    spanImage & spanImage.style.setProperty('--swatch-background', `url(${imgSrc})`);
                })
            }
        }
    }
    customElements.define('product-media-gallery', ProductMediaGallery);
}
